import { HeaderRefreshButton } from '../ListSelection'
/**
 * Reservas.
 *
 * Lo que se lista son los SERVICIOS reservables, no las citas: cada uno tiene
 * su propia página pública en `/book/<slug>` del sitio del cliente, que es
 * lo que de verdad se usa — un enlace que se manda y ya reserva.
 *
 * Un servicio reservable es una oferta con duración puesta. No hay tabla nueva
 * a propósito: tener «servicios» y «ofertas» por separado habría dejado dos
 * sitios donde poner un precio y dos cosas que sincronizar con Stripe.
 *
 * Los datos se cargan aquí porque la ficha necesita la misma copia que la
 * lista: si cada una pidiera la suya, guardar en la ficha dejaría la tabla
 * contando lo de antes. Mismo reparto que `PaymentsSection` con sus ofertas.
 */
import { useI18n } from '../../../lib/i18n'
import type { ProjectTheme } from '../../../lib/preview/types'
import { useEffect, useState } from 'react'

import { useBookingsData } from './useBookingsData'
import { SectionRefresh } from '../../SectionRefresh'
import { SectionLoader, useRecordLoader } from '../../RecordLoader'
import { DashboardPageHeader } from '../../DashboardPageHeader'
import { PlanUsageSummary } from '../PlanUsageSummary'
import { canConnectStripe, SITE_PLAN_LIMITS } from '../../../lib/planLimits'
import { BookingDetail } from './BookingDetail'
import { BookingServiceDetail } from './BookingServiceDetail'
import { BookingServicesPanel } from './BookingServicesPanel'
import { CatalogPageButton, LEGACY_CATALOG_PAGE_RECORD } from './CatalogPageButton'
import { NewBookingServiceDialog } from './NewBookingServiceDialog'
import { BookingTeamDialog } from './BookingTeamDialog'
import { IconUsers } from '@tabler/icons-react'
import { bookingError } from './bookingShared'
import { StripeConnectBanner } from './StripeConnectBanner'
import { StripeOnboardingDialog } from './StripeOnboardingDialog'
import { hasPaidOffer, publishBlockFor, type PublishBlock } from './offerShared'
import { useStripeConnect } from '../../../lib/stripeConnect'
import { can, type ProjectAccessRole } from '@webbuilder/shared-types'
import {
  updateOffer,
  type BillingPlan,
  type Offer,
} from '../../../lib/api'

/**
 * Lo que se está mirando, leído de la URL.
 *
 * Un id suelto es un servicio; `cita:<id>` es una cita concreta. Van por el
 * mismo parámetro para que el botón de atrás del navegador funcione en los dos
 * casos sin inventar una segunda ranura en la dirección.
 */
const BOOKING_PREFIX = 'cita:'

export function BookingsSection({ projectId, token, siteOrigin, record, plan, projectRole = 'owner', onRecordChange, onOpenPlans, onOpenPublish, projectTheme, onOpenContact, autoCreate = false, onAutoCreateHandled }: {
  projectId: string
  token: string
  /** Origen del sitio publicado. Vacío mientras no se haya publicado. */
  siteOrigin?: string | null | undefined
  record?: string | null | undefined
  /** El plan del sitio. Decide si publicar cobrando está siquiera al alcance. */
  plan: BillingPlan
  /** Para saber si además del calendario se puede tocar el catálogo. */
  projectRole?: ProjectAccessRole
  onRecordChange?: ((record: string | null) => void) | undefined
  /** Lleva al editor a publicar: sin sitio publicado no hay dirección de
   *  reserva, y publicar es lo único que la crea. */
  onOpenPublish?: (() => void) | undefined
  /** La paleta del editor, para la vista previa del índice público. */
  projectTheme?: ProjectTheme | undefined
  onOpenPlans: () => void
  onOpenContact?: ((identity: string) => void) | undefined
  /**
   * Abrir el alta de un servicio nada más llegar.
   *
   * Lo pide quien viene a crear su primera cita y todavía no tiene servicios:
   * el paso que le falta es éste, y hacérselo buscar en una pantalla que acaba
   * de aparecer sería devolverle el problema.
   */
  autoCreate?: boolean
  /** Se avisa al abrirlo para que la intención no sobreviva a la navegación. */
  onAutoCreateHandled?: (() => void) | undefined
}) {
  const { t } = useI18n()
  const data = useBookingsData(projectId, token)
  const { timezone, resources, offers, bookings, scheduleLoading, loading, failed, failureReason,
    setFailureReason, setResources, applyOffer, applyBooking, services } = data
  const loadOffers = data.reloadOffers
  const loadBookings = data.reloadBookings
  const [createOpen, setCreateOpen] = useState(false)
  /* El equipo: quiénes atienden. Ver `BookingTeamDialog`. */
  const [teamOpen, setTeamOpen] = useState(false)
  /* `record=page`: el enlace viejo a la pantalla de «Página pública», que ya
     no existe (`/bookings` se diseña en el editor). Se limpia. */
  useEffect(() => {
    if (record !== LEGACY_CATALOG_PAGE_RECORD) return
    onRecordChange?.(null)
  }, [record, onRecordChange])

  /* La intención de crear que llega desde Citas. Se apaga al atenderla para que
     no vuelva a abrirse al cerrar el formulario. */
  useEffect(() => {
    if (!autoCreate) return
    setCreateOpen(true)
    onAutoCreateHandled?.()
  }, [autoCreate, onAutoCreateHandled])
  const openingBooking = record?.startsWith(BOOKING_PREFIX) ? record.slice(BOOKING_PREFIX.length) : null
  const openingService = record && !openingBooking && record !== LEGACY_CATALOG_PAGE_RECORD ? record : null

  const service = openingService ? services.find((row) => row.id === openingService) ?? null : null
  const booking = openingBooking ? bookings.find((row) => row.id === openingBooking) ?? null : null
  const backFromService = () => { onRecordChange?.(null) }
  /* Lo que se ve DETRÁS de la ficha de una cita: su servicio. Es de donde se
     abre y es a donde devuelve cerrarla, así que dejarlo puesto es lo que hace
     que cerrar el diálogo no parezca un viaje. */
  const backdropService = service ?? (booking ? services.find((row) => row.id === booking.offerId) ?? null : null)

  /**
   * Un id que ya no corresponde a nada vuelve a la lista.
   *
   * Pasa con un enlace viejo y con el servicio al que se le acaba de quitar la
   * duración: sin esto la pantalla se quedaba en blanco sin decir por qué.
   */
  useEffect(() => {
    if (loading || data.refreshing || failed) return
    if (openingService && !services.some((row) => row.id === openingService)) onRecordChange?.(null)
  }, [openingService, services, loading, data.refreshing, failed, onRecordChange])

  /* El estado de la cuenta de cobros se pregunta AQUÍ y una sola vez: lo
     necesitan la lista y la ficha, y pidiéndolo cada una habría dos respuestas
     para la misma pregunta y dos momentos distintos en que se enteran. */
  const stripe = useStripeConnect(projectId, token, bookingError)

  /* La misma regla que la lista de ofertas: vive en `offerShared` porque es del
     servidor, y las dos pantallas tienen que contarla igual. */
  const blockFor = (offer: Offer): PublishBlock =>
    publishBlockFor(offer, {
      stripeReady: stripe.ready,
      canConnectStripe: plan !== 'free',
      plan,
      /* Publicadas de TODAS las familias: el cupo es del catálogo entero, así
         que una descarga publicada le quita el hueco a un servicio. */
      publishedOffers: offers.filter((row) => row.status === 'active').length,
    })

  /* La misma ventana de espera que Configuración → Stripe. Se pinta en las dos
     pantallas porque el alta se puede empezar desde cualquiera de las dos, y
     ninguna de ellas se desmonta mientras dura: ocurre en otra pestaña. */
  const stripeWait = stripe.awaiting
    ? <StripeOnboardingDialog status={stripe.status} onClose={stripe.dismiss} />
    : null

  const bookingLoader = useRecordLoader(Boolean(openingBooking) && !booking && (loading || data.refreshing))
  /* La lista espera a las dos consultas —los servicios y el horario—, que es lo
     que necesita `BookingServicesPanel` para pintarse entera. */
  const servicesLoader = useRecordLoader(loading || scheduleLoading || (Boolean(openingService) && !service && data.refreshing))


  // ── La ficha de una cita ───────────────────────────────────────────────────
  /* Ya no sustituye a nada: es un diálogo y se cuelga al final, encima de la
     pantalla desde la que se abrió. El cargador sí ocupa todo, porque viene de
     un enlace directo y todavía no hay nada detrás sobre lo que abrirse. */
  if (openingBooking && bookingLoader.visible) return <SectionLoader loader={bookingLoader} label={t('bookings.cal.loadingBooking')} />
  const detail = openingBooking && booking
    ? (
      <BookingDetail
          {...(() => {
            const offer = services.find((row) => row.id === booking.offerId)
            return {
              ...(offer?.images[0] ? { image: offer.images[0] } : {}),
              ...(offer ? { capacity: offer.booking.capacity } : {}),
              /* El equipo: con quién es la cita y a quién cambiarla. */
              team: resources,
            }
          })()}
        projectId={projectId}
        token={token}
        booking={booking}
        onClose={() => onRecordChange?.(booking.offerId ?? null)}
        onChange={applyBooking}
        onOpenContact={onOpenContact}
      />
    )
    : null

  // ── La ficha de un servicio ────────────────────────────────────────────────
  if (backdropService) {
    return (
      <>
      <BookingServiceDetail
        projectId={projectId}
        token={token}
        service={backdropService}
        publishBlock={blockFor(backdropService)}
        canConnectStripe={canConnectStripe(plan)}
        plan={plan}
        publishedOffers={offers.filter((row) => row.status === 'active').length}
        connectingStripe={stripe.connecting}
        onConnectStripe={() => { void stripe.connect() }}
        onOpenPlans={onOpenPlans}
        {...(onOpenPublish ? { onOpenPublish } : {})}
        bookings={bookings.filter((row) => row.offerId === backdropService.id)}
        resources={resources}
        timezone={timezone}
        siteOrigin={siteOrigin ?? ''}
        scheduleLoading={scheduleLoading}
        onBack={backFromService}
        onChange={applyOffer}
        onDeleted={(offerId) => {
          data.removeOffer(offerId)
          onRecordChange?.(null)
        }}
        onResourcesChange={setResources}
        onOpenBooking={(bookingId) => onRecordChange?.(`${BOOKING_PREFIX}${bookingId}`)}
        onBookingsChange={() => { void loadBookings() }}
        {...(projectTheme ? { theme: projectTheme } : {})}
      />
      {stripeWait}
      {detail}
      </>
    )
  }

  // ── La lista ───────────────────────────────────────────────────────────────
  /* La espera sustituye a la pantalla ENTERA, título incluido: estando sólo
     dentro del panel, la cabecera se pintaba sobre un hueco. Ver `SectionLoader`. */
  if (servicesLoader.visible) return <SectionLoader loader={servicesLoader} label={t('bookings.cal.loadingServices')} />

  return (
    <div className="dash-projects-view dash-pages-view dash-bookings-list-view dash-loaded-content-in">
      <SectionRefresh refreshing={data.refreshing} failed={failed} />
      <StripeConnectBanner
        overContent
        /* Las citas que se pagan en el lugar no piden Stripe: sólo las que se
           cobran al reservar. */
        hasPaidOffer={loading ? null : hasPaidOffer(services)}
        /* El estado de verdad también en el gratis: desde que cobra, forzarle
           «sin conectar» decía que no tenía cuenta a quien sí la tenía. */
        status={stripe.ready === null ? null : stripe.status}
        failed={Boolean(stripe.error)}
        detail={stripe.error || undefined}
        canConnect={canConnectStripe(plan)}
        onConnect={() => { void stripe.connect() }}
        onOpenPlans={onOpenPlans}
        onRetry={() => { void stripe.recheck() }}
      />

      <DashboardPageHeader
        title={t('bookings.cal.sectionTitle')}
        subtitle={t('bookings.services.subtitle', { timezone })}
        actionLayout="split"
        /* El cupo de servicios, como en Páginas: en la cabecera y no dentro de
           un aviso, porque no es una advertencia — es un dato del plan que se
           consulta de reojo mientras se trabaja. Cuenta SERVICIOS, no citas: el
           tope está en cuántas cosas ofreces, no en cuánto te reservan. */
        actions={(
          <div className="dash-bookings-header-actions">
            <div className="dash-bookings-header-tools">
              <HeaderRefreshButton refreshing={data.refreshing} onRefresh={data.refresh} />
              {/* Con texto, como «Editar categoría» de al lado: un icono de
                  personas solo no decía que ahí se da de alta al equipo. */}
              <button type="button" className="dash-secondary-btn dash-catalog-page-btn dash-team-button" aria-label={`${t('bookings.team.title')} (${resources.length})`} onClick={() => setTeamOpen(true)}>
                {resources.length ? (
                  <span className="dash-team-button-faces" aria-hidden="true">
                    {resources.slice(0, 3).map((person) => (
                      <span key={person.id} className="dash-team-button-face">
                        {person.photoUrl ? <img src={person.photoUrl} alt="" /> : person.name.trim().split(/\s+/).slice(0, 2).map((part) => part[0] ?? '').join('').toUpperCase() || <IconUsers size={12} />}
                      </span>
                    ))}
                  </span>
                ) : <IconUsers size={15} aria-hidden="true" />}
                {resources.length > 3 ? <span className="dash-team-button-more" aria-hidden="true">+{resources.length - 3}</span> : null}
                <span>{t('bookings.team.title')}</span>
              </button>
            {/* La página que reúne estos servicios: se diseña en el editor. */}
            <CatalogPageButton projectId={projectId} kind="bookings" />
            </div>
            {/* El cupo es del catálogo entero y cuenta PUBLICADAS: un servicio
                con agenda gasta el mismo hueco que un producto digital, así que
                aquí se enseña el mismo número que en Ventas. Contar sólo los
                servicios de esta pantalla diría que queda sitio donde no queda.
                `offers` las trae todas; `services` es sólo la vista de ésta. */}
            <div className="dash-bookings-header-usage">
              <PlanUsageSummary
                items={[{
                  label: t('usage.stat.offers'),
                  used: offers.filter((offer) => offer.status === 'active').length,
                  limit: SITE_PLAN_LIMITS[plan].offers,
                }]}
                plan={plan}
                onOpenPlans={onOpenPlans}
              />
            </div>
          </div>
        )}
      />

      <BookingServicesPanel
        projectId={projectId}
        services={services}
        bookings={bookings}
        failed={failed && offers.length === 0}
        failureReason={failureReason}
        siteOrigin={siteOrigin ?? ''}
        onReload={() => { void loadOffers() }}
        onOpen={(offerId) => onRecordChange?.(offerId)}
        /* `bookings.write` basta: un servicio reservable es contenido de la
           agenda, no de la tienda. El servidor no se fía de esto —comprueba que
           lo creado NAZCA reservable y que lo editado SIGA siéndolo, para que
           nadie cuele un producto de pago quitándole la duración— pero la
           pantalla no tiene por qué esconder lo que sí se puede hacer. */
        canEditCatalog={can(projectRole, 'bookings.write')}
        blockFor={blockFor}
        connectingStripe={stripe.connecting}
        onConnectStripe={() => { void stripe.connect() }}
        onOpenPlans={onOpenPlans}
        onCreate={() => setCreateOpen(true)}
        onStatusChange={async (service, status) => {
          try {
            applyOffer(await updateOffer(projectId, service.id, { status }, token))
          } catch (error) {
            setFailureReason(bookingError(error))
          }
        }}
      />

      {stripeWait}

      {teamOpen ? (
        <BookingTeamDialog
          projectId={projectId}
          token={token}
          resources={resources}
          onClose={() => setTeamOpen(false)}
          onReload={data.reloadSchedule}
        />
      ) : null}

      {createOpen ? (
        <NewBookingServiceDialog
          projectId={projectId}
          token={token}
          siteOrigin={siteOrigin ?? ''}
          timezone={timezone}
          resources={resources}
          plan={plan}
          publishedOffers={offers.filter((row) => row.status === 'active').length}
          onResourcesChange={setResources}
          onClose={() => setCreateOpen(false)}
          onCreated={(created) => {
            setCreateOpen(false)
            data.addOffer(created)
            // Se entra derecho a la ficha: acabar el alta y quedarse en la lista
            // dejaría a medias lo que falta —horario y página— sin decirlo.
            onRecordChange?.(created.id)
          }}
          canConnectStripe={canConnectStripe(plan)}
          onOpenPlans={onOpenPlans}
        />
      ) : null}

      {detail}
    </div>
  )
}
